import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, FileText, ExternalLink, History, Trash2, Pencil, CalendarDays, Search, X, ScanLine } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, formatDateOnly } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { FileUpload } from "@/components/FileUpload";
import { GoogleDrivePicker } from "@/components/GoogleDrivePicker";
import { QueryError } from "@/components/QueryError";

type DocType = "REGLAMENTO" | "MANUAL_NORMAS" | "ESCRITURA_MATRIZ" | "PLAN_EMERGENCIA" | "PLAN_RACIONAMIENTO" | "SEGURO" | "CONTRATO" | "ACTA_ASAMBLEA" | "ACTA_JUNTA" | "CONVOCATORIA" | "RESOLUCION" | "COMUNICADO" | "PRESUPUESTO" | "INFORME" | "OTRO";
type RegistryStatus = "NOT_REGISTERED" | "PENDING" | "REGISTERED";

interface Document {
  id: string;
  title: string;
  documentType: DocType;
  version: string;
  fileUrl: string;
  effectiveDate?: string | null;
  registryStatus: RegistryStatus;
  isCurrentVersion: boolean;
  createdAt: string;
  /** Set when the document is a meeting's acta — links back to the meeting. */
  meetingId?: string | null;
  meeting?: { id: string; title: string; scheduledAt: string } | null;
}

interface SearchHit {
  id: string;
  title: string;
  documentType: DocType;
  version: string;
  fileUrl: string;
  isCurrentVersion: boolean;
  /** "ocr" means the text was machine-read from a scan, not a real text layer. */
  contentSource: string | null;
  contentPages: number | null;
  /** Already HTML-escaped server-side; only <mark> is real markup. */
  snippet: string;
}

interface MeetingOption {
  id: string;
  title: string;
  scheduledAt: string;
}

const DOC_TYPES: DocType[] = ["REGLAMENTO", "MANUAL_NORMAS", "ESCRITURA_MATRIZ", "PLAN_EMERGENCIA", "PLAN_RACIONAMIENTO", "SEGURO", "CONTRATO", "ACTA_ASAMBLEA", "ACTA_JUNTA", "CONVOCATORIA", "RESOLUCION", "COMUNICADO", "PRESUPUESTO", "INFORME", "OTRO"];

const registryVariant: Record<RegistryStatus, "default" | "warning" | "success"> = {
  NOT_REGISTERED: "default",
  PENDING: "warning",
  REGISTERED: "success",
};

export function DocumentsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ title: string; documentType: DocType } | null>(null);
  const [editDoc, setEditDoc] = useState<Document | null>(null);
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<string>(searchParams.get("type") ?? "");
  const [showAll, setShowAll] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Debounced so typing does not fire a query per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const searching = searchQuery.length >= 2;

  const { data: documents = [], isLoading, isError } = useQuery({
    queryKey: ["documents", filter, showAll],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filter) params.append("documentType", filter);
      if (!showAll) params.append("currentOnly", "true");
      return (await api.get<Document[]>(`/documents?${params.toString()}`)).data;
    },
  });

  const { data: searchHits = [], isLoading: searchLoading } = useQuery({
    queryKey: ["documents", "search", searchQuery],
    enabled: searching,
    queryFn: async () =>
      (await api.get<SearchHit[]>(`/documents/search?q=${encodeURIComponent(searchQuery)}`)).data,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/documents/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  });

  const canUpload = user?.role === "ADMIN" || user?.role === "BOARD_MEMBER";
  const canDelete = user?.role === "ADMIN";

  // Group by type
  const grouped = documents.reduce<Record<string, Document[]>>((acc, d) => {
    (acc[d.documentType] ??= []).push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText size={24} /> {t("legal.documents")}
        </h1>
        {canUpload && (
          <button onClick={() => { setPrefill(null); setOpen(true); }} className="btn-primary">
            <Plus size={16} /> {t("legal.uploadDocument")}
          </button>
        )}
      </div>

      <div className="card space-y-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="search"
            // Hide WebKit's own clear affordance; this input already has one, and two
            // overlapping crosses look like a bug.
            className="input w-full pl-9 pr-9 [&::-webkit-search-cancel-button]:appearance-none"
            placeholder={t("legal.searchDocuments")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              aria-label={t("legal.searchClear")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Type and version filters apply to browsing, not to search results. */}
        {!searching && (
          <div className="flex flex-wrap gap-3 items-center">
            <select className="input max-w-xs" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">Todos los tipos</option>
              {DOC_TYPES.map((d) => <option key={d} value={d}>{t(`legal.docTypes.${d}`)}</option>)}
            </select>
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              Mostrar todas las versiones
            </label>
          </div>
        )}
      </div>

      {searching && (
        searchLoading ? (
          <div className="text-slate-500">{t("common.loading")}</div>
        ) : searchHits.length === 0 ? (
          <div className="card text-slate-500">{t("legal.searchNoResults", { query: searchQuery })}</div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">{t("legal.searchResultsCount", { count: searchHits.length })}</p>
            {searchHits.map((hit) => (
              <div key={hit.id} className="card space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{hit.title}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <Badge variant="info">{t(`legal.docTypes.${hit.documentType}`)}</Badge>
                      <span className="text-xs text-slate-500">v{hit.version}</span>
                      {hit.contentSource === "ocr" && (
                        <span title={t("legal.searchOcrTitle")}>
                          <Badge variant="warning">
                            <ScanLine size={11} className="mr-1" /> {t("legal.searchOcrBadge")}
                          </Badge>
                        </span>
                      )}
                    </div>
                  </div>
                  <a
                    href={hit.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary shrink-0"
                    title={t("legal.openDocument")}
                  >
                    <ExternalLink size={14} />
                  </a>
                </div>
                {/* Escaped server-side; only <mark> survives as markup. */}
                <p
                  className="text-sm text-slate-600 leading-relaxed [&_mark]:bg-amber-200 [&_mark]:text-slate-900 [&_mark]:rounded [&_mark]:px-0.5"
                  dangerouslySetInnerHTML={{ __html: hit.snippet }}
                />
              </div>
            ))}
          </div>
        )
      )}

      {searching ? null : isError ? (
        <QueryError />
      ) : isLoading ? (
        <div className="text-slate-500">{t("common.loading")}</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="card text-slate-500">—</div>
      ) : (
        Object.entries(grouped).map(([type, docs]) => (
          <div key={type} className="card">
            <h2 className="text-lg font-semibold mb-3">{t(`legal.docTypes.${type as DocType}`)}</h2>
            <table className="w-full text-sm">
              <thead className="text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left py-1">Título</th>
                  <th className="text-left py-1">{t("legal.version")}</th>
                  <th className="text-left py-1">{t("legal.effectiveDate")}</th>
                  <th className="text-left py-1">{t("legal.registry")}</th>
                  <th className="text-right py-1"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {docs.map((d) => (
                  <tr key={d.id}>
                    <td className="py-2">
                      <div className="font-medium">{d.title}</div>
                      <div className="text-xs text-slate-500">Subido {formatDate(d.createdAt, i18n.language)}</div>
                      {d.meeting && (
                        <Link to={`/meetings/${d.meeting.id}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-0.5">
                          <CalendarDays size={11} /> {d.meeting.title} · {formatDate(d.meeting.scheduledAt, i18n.language)}
                        </Link>
                      )}
                    </td>
                    <td className="py-2">
                      <Badge variant={d.isCurrentVersion ? "success" : "default"}>
                        v{d.version}{d.isCurrentVersion && ` · ${t("legal.currentVersion")}`}
                      </Badge>
                    </td>
                    <td className="py-2">{formatDateOnly(d.effectiveDate, i18n.language)}</td>
                    <td className="py-2"><Badge variant={registryVariant[d.registryStatus]}>{t(`legal.registryStatus.${d.registryStatus}`)}</Badge></td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <a href={d.fileUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm inline-flex items-center gap-1 mr-3">
                        <ExternalLink size={12} /> {t("legal.open")}
                      </a>
                      {canUpload && (
                        <>
                          <button
                            className="text-slate-600 hover:text-primary text-sm inline-flex items-center gap-1 mr-3"
                            onClick={() => setEditDoc(d)}
                            title={t("legal.edit")}
                          >
                            <Pencil size={12} /> {t("legal.edit")}
                          </button>
                          <button
                            className="text-slate-600 hover:text-primary text-sm inline-flex items-center gap-1 mr-3"
                            onClick={() => { setPrefill({ title: d.title, documentType: d.documentType }); setOpen(true); }}
                            title={t("legal.newVersion")}
                          >
                            <History size={12} /> {t("legal.newVersion")}
                          </button>
                        </>
                      )}
                      {canDelete && (
                        <button
                          className="text-red-600 hover:text-red-700 text-sm"
                          onClick={() => { if (confirm("¿Eliminar documento?")) deleteMutation.mutate(d.id); }}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      {open && (
        <UploadModal
          prefill={prefill}
          onClose={() => setOpen(false)}
          onDone={() => qc.invalidateQueries({ queryKey: ["documents"] })}
        />
      )}
      {editDoc && (
        <EditModal
          doc={editDoc}
          onClose={() => setEditDoc(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ["documents"] })}
        />
      )}
    </div>
  );
}

function UploadModal({ prefill, onClose, onDone }: { prefill: { title: string; documentType: DocType } | null; onClose: () => void; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const isNewVersion = !!prefill;
  const [form, setForm] = useState({
    title: prefill?.title ?? "",
    documentType: prefill?.documentType ?? ("REGLAMENTO" as DocType),
    version: prefill ? "" : "1.0",
    fileUrl: "",
    effectiveDate: "",
    registryStatus: "NOT_REGISTERED" as RegistryStatus,
    meetingId: "",
  });
  // Board/admin only reach this modal, and they may list meetings.
  const { data: meetings = [] } = useQuery({
    queryKey: ["meetings-options"],
    queryFn: async () => (await api.get<MeetingOption[]>("/meetings")).data,
  });

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post("/documents/upload", {
        ...form,
        meetingId: form.meetingId || null,
        effectiveDate: form.effectiveDate ? new Date(form.effectiveDate).toISOString() : null,
      })).data,
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Modal open onClose={onClose} title={isNewVersion ? t("legal.newVersion") : t("legal.uploadDocument")} size="lg">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <div>
          <label className="label">Título</label>
          <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("legal.documentType")}</label>
            <select className="input" value={form.documentType} onChange={(e) => setForm({ ...form, documentType: e.target.value as DocType })}>
              {DOC_TYPES.map((d) => <option key={d} value={d}>{t(`legal.docTypes.${d}`)}</option>)}
            </select>
          </div>
          <div>
            <label className="label">{t("legal.version")}</label>
            <input className="input" required value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("legal.effectiveDate")}</label>
            <input type="date" className="input" value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("legal.registry")}</label>
            <select className="input" value={form.registryStatus} onChange={(e) => setForm({ ...form, registryStatus: e.target.value as RegistryStatus })}>
              {(["NOT_REGISTERED", "PENDING", "REGISTERED"] as RegistryStatus[]).map((r) => (
                <option key={r} value={r}>{t(`legal.registryStatus.${r}`)}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="label">{t("meetings.title")} <span className="text-slate-400 font-normal">(opcional)</span></label>
          <select className="input" value={form.meetingId} onChange={(e) => setForm({ ...form, meetingId: e.target.value })}>
            <option value="">—</option>
            {meetings.map((m) => (
              <option key={m.id} value={m.id}>
                {m.title} · {formatDate(m.scheduledAt, i18n.language)}
              </option>
            ))}
          </select>
        </div>
        <FileUpload label={t("legal.uploadFile")} folder="costadorada1/documents" value={form.fileUrl} onUploaded={(url) => setForm({ ...form, fileUrl: url })} />
        <GoogleDrivePicker folder="costadorada1/documents" onUploaded={(url) => setForm({ ...form, fileUrl: url })} />
        <p className="text-xs text-slate-500">Al subir, las versiones previas con el mismo título y tipo serán marcadas como no actuales.</p>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={!form.fileUrl || mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}

function EditModal({ doc, onClose, onDone }: { doc: Document; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: doc.title,
    documentType: doc.documentType,
    version: doc.version,
    fileUrl: doc.fileUrl,
    effectiveDate: doc.effectiveDate ? doc.effectiveDate.slice(0, 10) : "",
    registryStatus: doc.registryStatus,
  });

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.put(`/documents/${doc.id}`, {
        title: form.title,
        documentType: form.documentType,
        version: form.version,
        fileUrl: form.fileUrl,
        effectiveDate: form.effectiveDate ? new Date(form.effectiveDate).toISOString() : null,
        registryStatus: form.registryStatus,
      })).data,
    onSuccess: () => { onDone(); onClose(); },
  });

  const fileReplaced = form.fileUrl !== doc.fileUrl;

  return (
    <Modal open onClose={onClose} title={t("legal.editDocument")} size="lg">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <div>
          <label className="label">Título</label>
          <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("legal.documentType")}</label>
            <select className="input" value={form.documentType} onChange={(e) => setForm({ ...form, documentType: e.target.value as DocType })}>
              {DOC_TYPES.map((d) => <option key={d} value={d}>{t(`legal.docTypes.${d}`)}</option>)}
            </select>
          </div>
          <div>
            <label className="label">{t("legal.version")}</label>
            <input className="input" required value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("legal.effectiveDate")}</label>
            <input type="date" className="input" value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("legal.registry")}</label>
            <select className="input" value={form.registryStatus} onChange={(e) => setForm({ ...form, registryStatus: e.target.value as RegistryStatus })}>
              {(["NOT_REGISTERED", "PENDING", "REGISTERED"] as RegistryStatus[]).map((r) => (
                <option key={r} value={r}>{t(`legal.registryStatus.${r}`)}</option>
              ))}
            </select>
          </div>
        </div>
        <FileUpload label={t("legal.replaceFile")} folder="costadorada1/documents" value={form.fileUrl} onUploaded={(url) => setForm({ ...form, fileUrl: url })} />
        <GoogleDrivePicker folder="costadorada1/documents" onUploaded={(url) => setForm({ ...form, fileUrl: url })} />
        <p className="text-xs text-slate-500">
          {fileReplaced ? t("legal.replaceFileNote") : t("legal.editNote")}
        </p>
        {mutation.isError && <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={!form.fileUrl || mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
